import {
  forwardRef,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";

const base =
  "w-full rounded-[var(--radius-md)] border bg-ink-950/60 px-4 py-3 text-sm text-ink-50 " +
  "placeholder:text-ink-500 transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal-400/70 focus-visible:border-signal-400";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, ...rest }, ref) => (
    <input
      ref={ref}
      className={cn(
        base,
        "h-12",
        invalid ? "border-danger focus-visible:ring-danger/70 focus-visible:border-danger" : "border-ink-700",
        className,
      )}
      {...rest}
    />
  ),
);
Input.displayName = "Input";

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, ...rest }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        base,
        "min-h-32 resize-y",
        invalid ? "border-danger focus-visible:ring-danger/70 focus-visible:border-danger" : "border-ink-700",
        className,
      )}
      {...rest}
    />
  ),
);
Textarea.displayName = "Textarea";

/** Mono uppercase field label with an index tick, e.g. "01 / Name". */
export function FieldLabel({ index, children }: { index?: string; children: ReactNode }) {
  return (
    <label className="mb-2 flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-ink-400">
      {index && <span className="text-signal-500">{index}</span>}
      {children}
    </label>
  );
}
